import { Notification } from "@/utils/notificationUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { calcSettingsApi } from "../../../modules/calculator/settings/api";
import {
  CalcSettings,
  CalcSettingsRequest,
  CreateCalcSettings,
  UpdateCalcSettings,
} from "../../../modules/calculator/settings/types";
import { UUID } from "../../../typings/global";
import { EntityObject, TwoLevelEntityIdObject } from "../../types";

export const CALC_SETTINGS_QUERY_KEYS = {
  listCalcSettings: (payload?: CalcSettingsRequest) => (payload ? ["listCalcSettings", payload] : ["listCalcSettings"]),
  getCalcSettingsDetail: (id: UUID) => ["getCalcSettingsDetail", id],
  createCalcSettings: () => ["createCalcSettings"],
  updateCalcSettings: () => ["updateCalcSettings"],
  deleteCalcSettings: () => ["deleteCalcSettings"],
  uploadCalcSettingsAttachmentConfs: () => ["uploadCalcSettingsAttachmentConfs"],
  deleteCalcSettingsAttachmentConf: () => ["deleteCalcSettingsAttachmentConf"],
};

export const useListCalsSettings = (payload: CalcSettingsRequest) => {
  return useQuery({
    queryKey: CALC_SETTINGS_QUERY_KEYS.listCalcSettings(payload),
    queryFn: async () => {
      const { data } = await calcSettingsApi.listCalcSettings(payload);

      return data;
    },
    enabled: !!payload.calcType,
  });
};

export const useGetCalcSettingsDetail = (id: UUID) => {
  return useQuery({
    queryKey: CALC_SETTINGS_QUERY_KEYS.getCalcSettingsDetail(id),
    queryFn: async () => {
      const { data } = await calcSettingsApi.getCalcSettings({ id });

      return data;
    },
  });
};

export const useCreateCalcSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_SETTINGS_QUERY_KEYS.createCalcSettings(),
    mutationFn: async (payload: CreateCalcSettings) => {
      const { data } = await calcSettingsApi.createCalcSettings(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALC_SETTINGS_QUERY_KEYS.listCalcSettings() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCalcSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_SETTINGS_QUERY_KEYS.updateCalcSettings(),
    mutationFn: async (payload: EntityObject<UpdateCalcSettings>) => {
      const { data } = await calcSettingsApi.updateCalcSettings(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CALC_SETTINGS_QUERY_KEYS.listCalcSettings() });
      queryClient.setQueryData(CALC_SETTINGS_QUERY_KEYS.getCalcSettingsDetail(mutatedPayload.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCalcSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_SETTINGS_QUERY_KEYS.deleteCalcSettings(),
    mutationFn: (id: UUID) => {
      return calcSettingsApi.deleteCalcSettings({ id });
    },
    onSuccess: (_, mutatePayload) => {
      queryClient.invalidateQueries({ queryKey: CALC_SETTINGS_QUERY_KEYS.listCalcSettings() });
      queryClient.removeQueries({ queryKey: CALC_SETTINGS_QUERY_KEYS.getCalcSettingsDetail(mutatePayload) });
      Notification.itemDeletedNotification();
    },
  });
};

export const useUploadCalcSettingsAttachmentConfs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_SETTINGS_QUERY_KEYS.uploadCalcSettingsAttachmentConfs(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await calcSettingsApi.uploadAttachmentConfs(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        CALC_SETTINGS_QUERY_KEYS.getCalcSettingsDetail(mutatedPayload.id),
        (oldData: CalcSettings): CalcSettings => ({
          ...oldData,
          attachmentConfs: data,
        }),
      );
      Notification.itemCreatedNotification();
    },
  });
};

export const useDeleteCalcSettingsAttachmentConf = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_SETTINGS_QUERY_KEYS.deleteCalcSettingsAttachmentConf(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return calcSettingsApi.deleteAttachmentConf(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        CALC_SETTINGS_QUERY_KEYS.getCalcSettingsDetail(mutatedPayload.id1),
        (oldData: CalcSettings): CalcSettings => ({
          ...oldData,
          attachmentConfs: oldData.attachmentConfs.filter((conf) => conf.id !== mutatedPayload.id2),
        }),
      );
      Notification.itemDeletedNotification();
    },
  });
};
